import React, { useEffect, useMemo, useState } from "react";
import { api, speak } from "@/lib/kielo";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Volume2, ChevronLeft, ChevronRight, Check, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Flashcards() {
  const { user, progress, toggleWord, login } = useApp();
  const { t, lang } = useI18n();
  const [cards, setCards] = useState([]);
  const [category, setCategory] = useState("all");
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const mastered = new Set(progress?.mastered_words || []);

  useEffect(() => {
    api.get("/flashcards", { params: { lang } }).then(({ data }) => setCards(data));
  }, [lang]);

  const categories = useMemo(() => {
    const set = [...new Set(cards.map((c) => c.category))];
    return ["all", ...set];
  }, [cards]);

  const deck = useMemo(
    () => (category === "all" ? cards : cards.filter((c) => c.category === category)),
    [cards, category]
  );

  useEffect(() => {
    setIndex(0);
    setFlipped(false);
  }, [category]);

  if (!cards.length) return <div className="max-w-3xl mx-auto px-4 py-20 text-[#52635B]">{t("lesson.loading")}</div>;

  const card = deck[index] || deck[0];
  const isMastered = card && mastered.has(card.id);

  const go = (dir) => {
    setFlipped(false);
    setTimeout(() => {
      setIndex((p) => (p + dir + deck.length) % deck.length);
    }, 120);
  };

  const rate = async (know) => {
    if (!user) {
      login();
      return;
    }
    try {
      await toggleWord(card.id);
      toast[know ? "success" : "message"](know ? `"${card.fi}" ${t("flash.savedLearned")}` : `"${card.fi}" ${t("flash.savedReview")}`);
      if (know) setTimeout(() => go(1), 250);
    } catch (e) {
      toast.error("Tallennus epäonnistui.");
    }
  };

  const masteredInDeck = deck.filter((c) => mastered.has(c.id)).length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="text-center mb-8">
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0E382B] tracking-tight">{t("flash.title")}</h1>
        <p className="mt-2 text-[#52635B]">{t("flash.sub")} {user && `${t("flash.learnedCount")}: ${masteredInDeck}/${deck.length}`}</p>
      </div>

      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              category === c
                ? "bg-[#0E382B] text-white border-[#0E382B]"
                : "bg-white text-[#52635B] border-[#E2DFD7] hover:border-[#B5B0A2]"
            }`}
          >
            {c === "all" ? t("flash.all") : c}
          </button>
        ))}
      </div>

      {/* card */}
      <div className="flip-card h-72 sm:h-80 cursor-pointer" data-testid="flashcard-flip-container" onClick={() => setFlipped((f) => !f)}>
        <div className={`flip-inner ${flipped ? "is-flipped" : ""}`}>
          {/* front */}
          <div className="flip-face rounded-3xl bg-white border border-[#E2DFD7] p-8 items-center justify-center text-center" style={{ boxShadow: "0 12px 30px rgba(14,56,43,0.07)" }}>
            {isMastered && (
              <span className="absolute top-4 right-4 text-[#0E5A35]"><Check className="w-5 h-5" /></span>
            )}
            <span className="text-xs uppercase tracking-wider text-[#52635B] mb-3">{card.pos}</span>
            <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-[#0E382B]">{card.fi}</h2>
            <button
              onClick={(e) => { e.stopPropagation(); speak(card.fi); }}
              className="mt-4 inline-flex items-center gap-1.5 text-[#1E429F] text-sm"
            >
              <Volume2 className="w-4 h-4" /> {t("lesson.listen")}
            </button>
            <p className="mt-6 text-sm text-[#52635B] italic max-w-sm">{card.example}</p>
            <span className="absolute bottom-4 text-xs text-[#B5B0A2]">{t("flash.tap")}</span>
          </div>
          {/* back */}
          <div className="flip-face flip-back rounded-3xl bg-[#0E382B] p-8 items-center justify-center text-center" style={{ boxShadow: "0 12px 30px rgba(14,56,43,0.2)" }}>
            <span className="text-xs uppercase tracking-wider text-[#E8F0EC]/70 mb-3">{t("flash.translation")}</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-white">{card.en}</h2>
            <p className="mt-5 text-[#E8F0EC]/90 text-sm max-w-sm italic">{card.exampleEn}</p>
            {card.note && (
              <p className="mt-4 text-xs text-[#FDEEE5] bg-white/10 px-3 py-1.5 rounded-full border border-white/20">{card.note}</p>
            )}
          </div>
        </div>
      </div>

      {/* controls */}
      <div className="mt-6 flex items-center justify-between">
        <button onClick={() => go(-1)} data-testid="flashcard-prev-button" className="w-11 h-11 rounded-full bg-white border border-[#E2DFD7] flex items-center justify-center text-[#0E382B] hover:bg-[#F1EFEB] transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-sm text-[#52635B] font-medium">{index + 1} / {deck.length}</span>
        <button onClick={() => go(1)} data-testid="flashcard-next-button" className="w-11 h-11 rounded-full bg-white border border-[#E2DFD7] flex items-center justify-center text-[#0E382B] hover:bg-[#F1EFEB] transition-colors">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="mt-6 flex justify-center gap-3">
        <Button onClick={() => rate(false)} data-testid="flashcard-rating-review" variant="outline" className="rounded-full border-[#E2DFD7] text-[#52635B] hover:bg-[#F1EFEB]">
          <RotateCcw className="w-4 h-4 mr-1.5" /> {t("flash.review")}
        </Button>
        <Button onClick={() => rate(true)} data-testid="flashcard-rating-know" className={`rounded-full text-white ${isMastered ? "bg-[#52635B] hover:bg-[#52635B]" : "bg-[#0E5A35] hover:bg-[#0b4a2c]"}`}>
          <Check className="w-4 h-4 mr-1.5" /> {isMastered ? t("flash.mastered") : t("flash.know")}
        </Button>
      </div>
    </div>
  );
}
